import * as Sentry from "@sentry/nextjs"
import { ObservableFiniteStateMachine, Identity, OMUniverse, OMReference } from "@apptivity-lab/firmament-node-sdk"
import { AffAttendeeProfile, AffMembershipPackage } from "@firmament-packages/aff"
import { CnxConsumerProfile, CnxSessionWallet } from "@firmament-packages/connectx"
import { Group } from "@firmament-packages/kernel"
import { Setting } from "@firmament-packages/settings"
import { fbTrackEvent } from "@lib"

declare namespace AuthRegisterStateMachine {
    interface Start {
        name: "start"
    }

    interface Presenting {
        name: "presenting"
        identity?: Identity
        attendeeProfile?: AffAttendeeProfile
    }

    interface Registering {
        name: "registering"
        identity: Identity
        attendeeProfile: AffAttendeeProfile
    }

    interface ShowingEmailConfirmation {
        name: "showingEmailConfirmation"
        identity: Identity
        attendeeProfile: AffAttendeeProfile
    }

    interface ShowingError {
        name: "showingError"
        error: Error
        identity: Identity
        attendeeProfile: AffAttendeeProfile
    }

    interface Completed {
        name: "completed"
        attendeeProfile?: AffAttendeeProfile
        identity?: Identity
        routeTo?: string
    }

    export type State = Start |
        Presenting |
        ShowingEmailConfirmation |
        Registering | ShowingError |
        Completed
}

export class AuthRegisterStateMachine extends ObservableFiniteStateMachine<AuthRegisterStateMachine.State> {

    constructor() {
        super({ name: "start" })
    }

    start() {
        switch (this.state.name) {
            case "start":
                // To prevent logged-in user from re-registering,
                // we set machine state to "completed" so that page routes logged-in user away from registration page.
                // In this case, the identity itself is not used. This may need to change in the future.
                this.state = OMUniverse.shared.currentUser?.isAuthenticated === true
                    ? { name: "completed" }
                    : { name: "presenting" }
                break
            case "presenting":
                this.state = { name: "presenting", identity: this.state.identity }
                break
            default:
                return
        }
    }

    showEmailConfirmation(identity: Identity, attendeeProfile: AffAttendeeProfile) {
        switch (this.state.name) {
            case "presenting":
            case "showingError":
                break
            default:
                return
        }

        this.state = {
            name: "showingEmailConfirmation",
            identity,
            attendeeProfile
        }
    }

    async register(identity: Identity, attendeeProfile: AffAttendeeProfile, autoSubscriptionEnabled: boolean = true) {
        switch (this.state.name) {
            case "showingEmailConfirmation":
            case "showingError":
                break
            default:
                return
        }

        this.state = { name: "registering", identity, attendeeProfile }

        try {
            OMUniverse.shared.touch(identity)

            // Sign-up with userpass identity pair so that in future
            // user can potentially opt to log in with password?
            const userpassIdentity = new Identity({
                identityType: "userpass",
                provider: "apptivitylab.com",
                identifier: identity.identifier,
                // Generate 8 digit random password
                challenge: Math.floor(Math.random() * 1000000000).toString(),
                user: identity.user
            })
            OMUniverse.shared.touch(userpassIdentity)

            await OMUniverse.shared.signUp([identity, userpassIdentity])

            await this.setupAttendeeProfile(identity, attendeeProfile, autoSubscriptionEnabled)
        } catch (error: unknown) {
            if (error instanceof Error) {
                if (error.message.startsWith("Identifier Taken")) {
                    this.fail(new Error("This email already has a registered account. Please log in instead."))
                } else {
                    Sentry.captureException(`Attendee registration error - ${error.message}`)
                    this.fail(error)
                }
            } else {
                Sentry.captureException(`Attendee registration error - ${JSON.stringify(error)}`)
                this.fail(new Error("An unexpected error has occurred. If this problem persists, please refresh the page and try again."))
            }
        }
    }

    async setupAttendeeProfile(identity: Identity, attendeeProfile: AffAttendeeProfile, autoSubscriptionEnabled: boolean = true) {
        switch (this.state.name) {
            case "registering":
                break
            default:
                return
        }

        // Ensure that attendee profile belongs and is
        // associated to the same user as the identities
        // being created.
        attendeeProfile.ownerUuid = identity.user
        attendeeProfile.user = identity.user

        OMUniverse.shared.touch(attendeeProfile)
        await attendeeProfile.save()

        // Registered consumers should be part of the default consumer group
        const defaultConsumerGroupId = JSON.parse(localStorage?.getItem("settings") || "[]")
            .filter((each: { key: string, value?: any  }) => each.key === "cnx_onboarding_consumer_default_group")
            ?.[0]
            ?.value

        const currentUserGroups = (attendeeProfile.user.actualObject?.groups || []).map((group) => group.id)
        if (defaultConsumerGroupId && !currentUserGroups.includes(defaultConsumerGroupId)) {
            const consumerGroupId = new OMReference(Group, defaultConsumerGroupId)
            const consumerGroup = consumerGroupId.actualObject || await consumerGroupId.load()

            await consumerGroup?.addMember(attendeeProfile.user.id)
        }

        // Registered attendee should have a session wallet
        const sessionWallet = new CnxSessionWallet({
            currency: "SSN",
            consumer: new OMReference(CnxConsumerProfile, attendeeProfile)
        })
        OMUniverse.shared.touch(sessionWallet)
        await sessionWallet.save()
        OMUniverse.shared.touch(sessionWallet, true)
        attendeeProfile.sessionWallets = [new OMReference(CnxSessionWallet, sessionWallet.id)]

        // Persist profile in universe cache
        OMUniverse.shared.touch(attendeeProfile, true)

        const user = OMUniverse.shared.currentUser!.user
        const userProfiles = (user.profiles || [])

        userProfiles.push(new OMReference(AffAttendeeProfile, attendeeProfile.id))

        await attendeeProfile.registerStripeCustomer(
            attendeeProfile.fullName, // name
            undefined, // phone
            identity.identifier // email
        )

        if (autoSubscriptionEnabled) {
            const membershipPackageQuery = await AffMembershipPackage.query()
                .filter("trialDays", "moreThan", 0)
                .filter("isAvailable", "equals", true)
                .filter("publishAt", "lessThanEqual", new Date().toISOString() as any)
                .filterGroup([
                    ["unpublishAt", "moreThan", new Date().toISOString() as any],
                    ["unpublishAt", "isNull", undefined]
                ], "OR")
                .sort("sequence", "ASC")
                .limit(1)
                .execute()

            if (membershipPackageQuery.resultObjects.length > 0) {
                const membershipPackage = membershipPackageQuery.resultObjects[0]
                await membershipPackage.startTrial(attendeeProfile.id, new Date())

                fbTrackEvent("StartTrial", {
                    value: (membershipPackage.actualPriceCents / 100.0).toFixed(2),
                    currency: "USD"
                })
            }
        }

        await attendeeProfile.loadActiveSubscriptions()

        user.profiles = userProfiles.map((each) => each)
        OMUniverse.shared.touch(user, true)

        this.success()
    }

    async retry(identity: Identity, attendeeProfile: AffAttendeeProfile) {
        switch (this.state.name) {
            case "showingError":
                return this.register(identity, attendeeProfile)
            default:
                return
        }
    }

    back() {
        switch (this.state.name) {
            case "showingEmailConfirmation":
                this.state = {
                    name: "presenting",
                    identity: this.state.identity,
                    attendeeProfile: this.state.attendeeProfile
                }
        }
    }

    private async success() {
        switch (this.state.name) {
            case "registering":
                break
            default:
                return
        }

        const currentSubscription = this.state.attendeeProfile.currentSubscription()
        let routeTo: string = "/welcome"

        if (!currentSubscription) {
            const settingsQuery = Setting.query()
                .filter("key", "equals", "aff_default_membership_package_id")
                .filterGroup([
                    ["platform", "isNull", undefined],
                    ["platform", "equals", "NextJS"]
                ], "OR")
                .limit(500)

            await settingsQuery.execute()

            const defaultMembershipPackageIdSetting = settingsQuery.resultObjects.find((setting) => setting.key === "aff_default_membership_package_id")

            if (defaultMembershipPackageIdSetting && defaultMembershipPackageIdSetting.value) {
                routeTo = `/profile/membership/payment?membershipPackage=${defaultMembershipPackageIdSetting.value}`
            }
        }

        this.state = {
            name: "completed",
            identity: this.state.identity,
            attendeeProfile: this.state.attendeeProfile,
            routeTo
        }
    }

    private fail(error: Error) {
        switch (this.state.name) {
            case "registering":
                this.state = {
                    name: "showingError",
                    error,
                    identity: this.state.identity,
                    attendeeProfile: this.state.attendeeProfile
                }
                break
            default:
                return
        }
    }
}
